import React, { FunctionComponent, useEffect, useState } from 'react';
import { BlockPicker, ColorResult } from 'react-color';
import classnames from 'classnames';
import withStyles from 'react-jss';
import { useFormContext } from 'react-hook-form';

import { ClassProp } from 'helpers/types';
import { isLight } from 'helpers/colorFunctions';
import { Theme } from 'config';
import { useColorThief } from 'helpers/useColorThief';

import FormErrors from '../FormError';

type Styles = ClassProp<ReturnType<typeof styles>>;

type Props = {
  classes: Styles;
  type: string;
  name: string;
  placeholder: string;
  label: string;
  register: any;
  defaultValue: string;
  size?: string;
  image?: string;
};

const styles = (theme: Theme) => ({
  container: {
    width: '100%',
    marginTop: '.25rem !important',
    justifyContent: 'center !important',
  },
  small: {
    width: '31%',
    marginRight: '.55rem',
  },
  medium: {
    width: '48%',
    marginRight: '.55rem',
  },
  large: {
    width: '100%',
  },
  fieldContainer: {},
  fieldLabel: {
    color: '#646464 !important',
    fontSize: '.875rem !important',
    fontWeight: '500 !important',
    margin: '0 0 .30rem 0',
    display: 'block !important',
  },
  fieldInput: {
    display: 'flex',
    flex: '1 0 auto',
    maxWidth: '100%',
    width: '100%',
    textAlign: 'left',
    margin: '0',
    outline: '0',
    tapHighlightColor: 'rgba(255,255,255,0)',
    lineHeight: '1.21em',
    padding: '.67857143rem 1rem',
    fontSize: '1em',
    background: '#fff',
    border: '.0625rem solid rgba(34,36,38,.15)',
    color: 'rgba(0,0,0,.87)',
    borderRadius: '.28571429rem',
    boxShadow: '0 0 0 0 transparent inset',
    transition: 'color .1s ease,border-color .1s ease',
  },
  color: {
    width: '5.25rem',
    height: '2rem',
    borderRadius: '.28571429rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  swatch: {
    padding: '.25rem',
    background: '#fff',
    borderRadius: '.28571429rem',
    boxShadow: '0 .125rem .375rem 0 rgba(0,0,0,0.15) !important',
    display: 'inline-block',
    cursor: 'pointer',
  },
  popover: {
    position: 'absolute',
    zIndex: '2',
  },
  cover: {
    position: 'fixed',
    top: '0px',
    right: '0px',
    bottom: '0px',
    left: '0px',
  },
  fieldError: {},
});

const getImage = (image: string | null, logo: string) => {
  if (image && !logo) return image;

  if (logo) {
    const { secure_url } = JSON.parse(logo);
    return secure_url;
  }

  return null;
};

const ControlledFormInput: FunctionComponent<Props> = ({
  classes,
  label,
  name,
  size,
  register,
  defaultValue,
  image = null,
  ...rest
}) => {
  const [showPicker, setShowPicker] = useState<boolean>(false);
  const [colorValue, setColorValue] = useState<any>(defaultValue);
  const [colorPalette, setColorPalette] = useState<any>([]);

  const {
    getValues,
    formState: { errors },
  } = useFormContext();
  const { getPalette } = useColorThief('anonymous');

  const handleClick = () => setShowPicker(true);
  const handleClose = () => setShowPicker(false);

  const handleChange = (color: ColorResult) => {
    const { hex } = color;
    setColorValue(hex);
  };

  useEffect(() => {
    const getColorPalette = async () => {
      const img = getImage(image, getValues('logo'));
      if (img) {
        const palette = await getPalette(img, 10, 10);
        setColorPalette(palette);
      }
    };
    getColorPalette();
  }, [showPicker]);

  return (
    <div
      className={classnames(classes.container, {
        [classes.small]: size === 'small',
        [classes.medium]: size === 'medium',
        [classes.large]: size === 'large',
      })}
    >
      <label htmlFor={name}>
        <span className={classes.fieldLabel}>{label}</span>
        <div className={classes.swatch} onClick={handleClick}>
          <div
            className={classes.color}
            style={{ backgroundColor: colorValue }}
          >
            <span style={{ color: isLight(colorValue) ? '#000' : '#fff' }}>
              {colorValue}
            </span>
          </div>
        </div>
        {showPicker && (
          <div className={classes.popover}>
            <div className={classes.cover} onClick={handleClose} />
            <BlockPicker
              color={colorValue}
              colors={colorPalette}
              onChange={handleChange}
            />
          </div>
        )}
        <input
          type="hidden"
          id={name}
          name={name}
          ref={register}
          value={colorValue}
        />
      </label>
      {errors && <FormErrors name={name} errors={errors} />}
    </div>
  );
};
export default withStyles(styles)(ControlledFormInput);
